import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { Loader2 } from 'lucide-react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { translateSystemError, isSystemError, logSystemError } from '@/lib/utils/errorTranslator';

const LoadingSpinner = () => (
  <div className='min-h-screen bg-background flex items-center justify-center'>
    <div className='text-center'>
      <Loader2 className='h-8 w-8 animate-spin mx-auto mb-4 text-primary' />
      <p className='text-muted-foreground'>Completing sign in...</p>
    </div>
  </div>
);

const ErrorDisplay = ({ error }: { error: string }) => (
  <div className='min-h-screen bg-background flex items-center justify-center'>
    <div className='text-center max-w-md'>
      <div className='bg-destructive/10 border border-destructive/20 rounded-lg p-6'>
        <h2 className='text-lg font-semibold text-destructive mb-2'>Sign In Failed</h2>
        <p className='text-sm text-muted-foreground mb-4'>{error}</p>
        <button
          onClick={() => (window.location.href = '/auth')}
          className='text-primary hover:text-primary/80 font-medium text-sm'
        >
          Try again
        </button>
      </div>
    </div>
  </div>
);

export const AuthCallback: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('🔄 AuthCallback: Starting OAuth callback processing...');

    // First, try to get the current session
    const checkInitialSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        console.log('📊 Initial session check:', { hasSession: !!data.session, error });

        if (error) {
          logSystemError(error, 'checking auth session');
          const userMessage = isSystemError(error)
            ? translateSystemError(error, 'checking auth session').message
            : error.message;
          setError(userMessage);
          setLoading(false);
          return;
        }

        if (data.session) {
          console.log('✅ Session found, redirecting...');
          setLoading(false);
          return;
        }

        // If no session yet, wait for auth state change
        console.log('⏳ No session yet, waiting for auth state change...');
      } catch (err) {
        logSystemError(err, 'checking auth session');
        const userMessage = isSystemError(err)
          ? translateSystemError(err, 'checking auth session').message
          : err instanceof Error
            ? err.message
            : 'Authentication failed';
        setError(userMessage);
        setLoading(false);
      }
    };

    // Listen for auth state changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      console.log('🔔 Auth state change:', event, session ? 'Session found' : 'No session');

      if (event === 'SIGNED_IN' && session) {
        console.log('✅ User signed in successfully');
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        console.log('❌ User signed out');
        setError('Authentication failed - user was signed out');
        setLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        console.log('🔄 Token refreshed');
        setLoading(false);
      }
    });

    // Check initial session
    checkInitialSession();

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.log('⏰ Auth callback timeout - no response received');
      setError('Authentication timeout - please try again');
      setLoading(false);
    }, 10000); // 10 second timeout

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return <Navigate to='/' replace />;
};
